import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fmtErr } from "../lib/api";
import { AuthShell } from "./Login";

const ROLE_OPTIONS = [
  { value: "buyer", label: "Buyer / Tenant", hint: "Save residences, request walkthroughs" },
  { value: "agent", label: "Agent / Broker", hint: "List mandates, receive routed leads" },
  { value: "builder", label: "Builder / Developer", hint: "Showcase projects at scale" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const initialRole = ["buyer", "agent", "builder"].includes(params.get("role")) ? params.get("role") : "buyer";
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "", company_address: "", role: initialRole });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const isPartner = form.role === "builder" || form.role === "agent";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const user = await register(form);
      if (user.status === "pending") {
        setPendingUser(user);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      setError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (pendingUser) {
    return (
      <AuthShell title="Under review." sub="" testid="register-pending-page">
        <div className="border border-gold/40 bg-gold/10 p-8" data-testid="pending-approval-notice">
          <CheckCircle2 size={22} className="text-gold" />
          <p className="mt-4 font-serif text-2xl text-bone">Application received, {pendingUser.name.split(" ")[0]}.</p>
          <p className="mt-3 text-sm font-light leading-relaxed text-bone/70">
            Every partner on Gurgaon Estates is verified by our curation desk. We'll activate your {pendingUser.role === "builder" ? "developer" : "broker"} desk shortly — you can sign in anytime to check your status.
          </p>
          <Link to="/dashboard" data-testid="pending-dashboard-link" className="gold-link mt-6 inline-block">Continue to your desk →</Link>
        </div>
      </AuthShell>
    );
  }

  const inputCls = "w-full border border-white/15 bg-transparent px-4 py-3.5 text-sm font-light text-bone outline-none transition-colors placeholder:text-smoke/60 focus:border-gold";

  return (
    <AuthShell title="Join the house." sub="Buyers enter instantly. Partners are verified by our curation desk." testid="register-page">
      <div className="mb-6 grid grid-cols-3 gap-2" data-testid="role-selector">
        {ROLE_OPTIONS.map((r) => (
          <button
            key={r.value}
            type="button"
            onClick={() => setForm({ ...form, role: r.value })}
            data-testid={`role-option-${r.value}`}
            title={r.hint}
            className={`border px-2 py-3 text-center font-mono text-[10px] uppercase tracking-[0.12em] transition-colors ${
              form.role === r.value ? "border-gold bg-gold/10 text-gold" : "border-white/15 text-smoke hover:border-white/40"
            }`}
          >
            {r.label}
          </button>
        ))}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="register-form">
        <input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="register-name-input" />
        <input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="register-email-input" />
        <input required type="password" minLength={6} placeholder="Password (min 6 characters)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="register-password-input" />
        <input placeholder="Phone (WhatsApp alerts)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} data-testid="register-phone-input" />
        {isPartner && (
          <>
            <input required placeholder={form.role === "builder" ? "Development firm" : "Brokerage / Agency"} value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className={inputCls} data-testid="register-company-input" />
            <textarea required rows={2} placeholder="Office address" value={form.company_address} onChange={(e) => setForm({ ...form, company_address: e.target.value })} className={`${inputCls} resize-none`} data-testid="register-address-input" />
            <p className="text-[11px] font-light text-smoke">Office name and address are mandatory for partners — verified by our curation desk.</p>
          </>
        )}
        {error && <p className="text-xs text-red-400" data-testid="register-error">{error}</p>}
        <button
          type="submit" disabled={busy} data-testid="register-submit-button"
          className="flex w-full items-center justify-center gap-2 bg-gold px-4 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-ink transition-colors hover:bg-bone disabled:opacity-50"
        >
          {busy ? "Creating…" : form.role === "buyer" ? "Create Account" : "Apply as Partner"} <ArrowRight size={14} />
        </button>
      </form>

      <div className="my-6 flex items-center gap-4" aria-hidden="true">
        <span className="h-px flex-1 bg-white/10" />
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-smoke">or</span>
        <span className="h-px flex-1 bg-white/10" />
      </div>

      <button
        onClick={() => {
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          const redirectUrl = window.location.origin + '/dashboard';
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        }}
        data-testid="google-register-button"
        className="flex w-full items-center justify-center gap-3 border border-white/20 px-4 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-bone transition-colors hover:border-gold hover:text-gold"
      >
        <svg width="15" height="15" viewBox="0 0 48 48" aria-hidden="true">
          <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
          <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
          <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
          <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C36.9 39.2 44 34 44 24c0-1.3-.1-2.6-.4-3.9z"/>
        </svg>
        Continue with Google
      </button>
      <p className="mt-3 text-center text-[11px] font-light text-smoke">Instant buyer account — partners use the form above.</p>

      <p className="mt-8 text-xs font-light text-smoke">
        Already hold a key? <Link to="/login" className="text-gold hover:text-bone" data-testid="register-login-link">Sign in</Link>
      </p>
    </AuthShell>
  );
}
